import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmt, rankByTier } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { TrophyIcon, CurrencyCircleDollarIcon } from "@phosphor-icons/react";
import BotName from "@/components/BotName";

// Podium tone per rank
const RANK_TONE = [
  { color: "#D6FF38", label: "1st" },
  { color: "#94A3B8", label: "2nd" },
  { color: "#B87333", label: "3rd" },
];

export default function SymbolLeaders() {
  const [bots, setBots] = useState([]);
  const [seededSymbols, setSeededSymbols] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/bots"),
      api.get("/symbols"),
    ]).then(([botsRes, symRes]) => {
      setBots(botsRes.data.bots || []);
      // Only take symbols flagged `seeded` (0 bots) - real symbols get grouped
      // from the /bots response below, so we don't double-count.
      setSeededSymbols((symRes.data.symbols || []).filter((s) => s.seeded));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const bySymbol = useMemo(() => {
    const groups = {};
    for (const b of bots) {
      const s = (b.symbol || "").trim();
      // Include graveyard bots too - the leaderboard should reflect the
      // full picture, not hide any symbol just because its top bot got
      // retired. The rankByTier sort still buries graveyard bots below
      // active tiers so the #1 spot stays meaningful.
      if (!s) continue;
      (groups[s] ||= []).push(b);
    }
    // Sort each symbol group by tier priority first (Champion > Elite > Basic
    // > Testing) then by growth_pct as tiebreaker. Keep top 3. This makes the
    // #1 podium spot always go to the highest-tier bot in that symbol.
    const withBots = Object.entries(groups)
      .map(([sym, arr]) => ({
        symbol: sym,
        bots: [...arr].sort(rankByTier).slice(0, 3),
        total_bots: arr.length,
        seeded: false,
      }))
      // Sort symbols by their #1 bot's tier+growth (best-tiered symbols first)
      .sort((a, b) => rankByTier(a.bots[0] || {}, b.bots[0] || {}));

    // Append seeded placeholders alphabetically at the bottom.
    const placeholders = [...seededSymbols]
      .sort((a, b) => a.symbol.localeCompare(b.symbol))
      .map((s) => ({ symbol: s.symbol, bots: [], total_bots: 0, seeded: true }));
    return [...withBots, ...placeholders];
  }, [bots, seededSymbols]);

  if (loading) return <div className="p-6 mono text-sm text-[#94A3B8]">Loading symbol leaders…</div>;

  return (
    <div data-testid="symbol-leaders-page" className="p-4 md:p-6 space-y-5">
      <div>
        <div className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/70">Per-Symbol Podium</div>
        <h1 className="text-xl md:text-2xl tracking-tight font-medium mt-1 text-[#F1F5F9]">Symbol Leaders</h1>
        <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl leading-relaxed">
          Top-3 best-performing bots for every symbol you&apos;ve tested. Symbols ordered by their #1 bot&apos;s growth — so
          your strongest markets bubble to the top.
        </p>
      </div>

      <div className="text-[11px] mono text-[#94A3B8]">
        {bySymbol.length} symbols · {bots.length} bots total
        {seededSymbols.length > 0 && (
          <span> · {seededSymbols.length} seeded (awaiting first backtest)</span>
        )}
      </div>

      <div className="space-y-4">
        {bySymbol.map(({ symbol, bots: top, total_bots, seeded }) => (
          <div
            key={symbol}
            data-testid={`symbol-block-${symbol}`}
            className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4 hover:border-[#3a3e4f] transition-colors"
            style={seeded ? { borderStyle: "dashed", opacity: 0.75 } : undefined}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <CurrencyCircleDollarIcon size={16} className="text-[#D6FF38]" weight="duotone" />
                <div className="mono text-lg text-[#F1F5F9]">{symbol}</div>
                {seeded ? (
                  <span
                    className="text-[10px] mono uppercase tracking-widest px-1.5 py-0.5 rounded"
                    style={{ color: "#D6FF38", background: "rgba(214,255,56,0.10)", border: "1px solid rgba(214,255,56,0.30)" }}
                    data-testid={`seeded-badge-${symbol}`}
                  >
                    seeded
                  </span>
                ) : (
                  <span className="text-[11px] mono text-[#94A3B8]">· {total_bots} bots tested</span>
                )}
              </div>
              {!seeded && (
                <Link
                  to={`/leaderboard?symbol=${encodeURIComponent(symbol)}`}
                  className="text-[10px] mono uppercase tracking-widest text-[#D6FF38] hover:underline"
                >
                  view all →
                </Link>
              )}
            </div>

            {seeded ? (
              <div className="border border-dashed border-[#2A2D3A]/60 rounded-md p-6 text-center">
                <div className="text-xs mono text-[#94A3B8]">
                  queued in the seed config — waiting for first VPS backtest to arrive
                </div>
              </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {top.map((b, i) => {
                const meta = RANK_TONE[i];
                return (
                  <Link
                    to={`/bot/${b.id}`}
                    key={b.id}
                    data-testid={`podium-${symbol}-${i + 1}`}
                    className="relative border rounded-md p-3 bg-[#0B0C10] hover:bg-[#181A22] transition-colors overflow-hidden block"
                    style={{ borderColor: meta.color + "44" }}
                  >
                    <div className="absolute inset-y-0 left-0 w-[3px]" style={{ background: meta.color }} />
                    <div className="flex items-center justify-between pl-1">
                      <div className="inline-flex items-center gap-1.5">
                        <TrophyIcon size={12} weight="fill" style={{ color: meta.color }} />
                        <span className="text-[10px] mono uppercase tracking-widest" style={{ color: meta.color }}>{meta.label}</span>
                      </div>
                      <StatusBadge status={b.status} />
                    </div>
                    <div className="mono text-sm text-[#F1F5F9] mt-2 truncate pl-1" title={b.name}>
                      <BotName name={b.name} maxLen={28} showDepth={false} />
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-3 pl-1">
                      <div>
                        <div className="text-[9px] mono uppercase tracking-widest text-[#94A3B8]">Growth</div>
                        <div className="mono text-sm" style={{ color: b.growth_pct >= 0 ? "#2ECA7F" : "#FF4D4D" }}>
                          {fmt.signed(b.growth_pct)}%
                        </div>
                      </div>
                      <div>
                        <div className="text-[9px] mono uppercase tracking-widest text-[#94A3B8]">Bal DD</div>
                        <div className="mono text-sm text-[#FF4D4D]">{b.balance_dd ?? b.max_drawdown ?? 0}%</div>
                      </div>
                      <div>
                        <div className="text-[9px] mono uppercase tracking-widest text-[#94A3B8]">Eq DD</div>
                        <div className="mono text-sm text-[#F97316]">{b.equity_dd ?? 0}%</div>
                      </div>
                      <div>
                        <div className="text-[9px] mono uppercase tracking-widest text-[#94A3B8]">PF</div>
                        <div className="mono text-sm text-[#F1F5F9]">{b.profit_factor ?? 0}</div>
                      </div>
                    </div>
                  </Link>
                );
              })}
              {Array.from({ length: 3 - top.length }).map((_, i) => (
                <div key={`empty-${i}`} className="border border-dashed border-[#2A2D3A]/60 rounded-md p-3 text-center">
                  <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">no {RANK_TONE[top.length + i]?.label} yet</div>
                </div>
              ))}
            </div>
            )}
          </div>
        ))}

        {bySymbol.length === 0 && (
          <div className="border border-dashed border-[#2A2D3A] rounded-md p-12 text-center text-[#94A3B8] mono text-sm">
            No symbols yet — bots will appear here as your fleet grows.
          </div>
        )}
      </div>
    </div>
  );
}
